import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { compare, duplicates, query } from './api'
import { toRequestSpec } from './filterSpec'

const PAGE_SIZE = 200
const MAX_CACHED_PAGES = 60
const POLL_MS = 500

/**
 * Fetches rows one page at a time and keeps at most MAX_CACHED_PAGES in memory,
 * so browsing a 10M-row file costs the same as browsing a 1k-row one.
 *
 * While a scan is still running the server keeps returning a growing match
 * count; we poll for that and drop partially-filled pages as more arrive.
 *
 * @param dup when set, `{columns, mode, resolution}` — rows come from the
 *   duplicate finder, which shows the duplicates, hides them, or lists what
 *   hiding them removes, depending on the mode; `resolution` picks which copy
 *   of a group survives
 * @param cmp when set, `{other, mode, spec}` — rows come from the comparison
 *   against an uploaded file instead. It sits *outside* `dup` rather than beside
 *   it: the thing being compared is whatever the other two produce, so both are
 *   sent along and the server walks their rows.
 */
export function useRowSource(datasetId, appliedFilters, dup, cmp) {
  const [status, setStatus] = useState({
    filtered: false,
    duplicates: false,
    matched: 0,
    available: 0,
    groups: 0,
    removed: 0,
    done: false,
    progress: 0,
    capped: false,
    error: null,
  })
  const [, bump] = useState(0)
  const pages = useRef(new Map())
  const inflight = useRef(new Set())
  const order = useRef([])
  const available = useRef(0)

  // Pruned and id-free: an unfinished group must not look like a new filter set.
  const filters = useMemo(() => toRequestSpec(appliedFilters), [appliedFilters])

  // Collapse the duplicate setup to a string so the identity is stable across
  // renders. JSON, not a join: a header can contain the separator.
  const dupKey = dup?.columns?.length
    ? JSON.stringify([dup.columns, dup.mode ?? 'duplicates', dup.resolution ?? null])
    : null
  const dupSetup = useMemo(() => {
    if (!dupKey) return null
    const [columns, mode, resolution] = JSON.parse(dupKey)
    return { columns, mode, resolution }
  }, [dupKey])

  // Same trick for the comparison: a stable string identity across renders.
  const cmpKey = cmp?.other && cmp?.spec ? JSON.stringify([cmp.other, cmp.mode ?? 'all', cmp.spec]) : null
  const cmpSetup = useMemo(() => {
    if (!cmpKey) return null
    const [other, mode, spec] = JSON.parse(cmpKey)
    return { other, mode, spec }
  }, [cmpKey])

  /**
   * Rows come from the comparison, the duplicate finder, or the plain filter
   * endpoint. The comparison is the outermost of the three, so it carries the
   * other two rather than replacing them.
   */
  const load = useCallback(
    (offset, limit) => {
      if (cmpSetup) {
        return compare({
          id: datasetId,
          other: cmpSetup.other,
          filters,
          dup: dupSetup,
          compare: cmpSetup.spec,
          mode: cmpSetup.mode,
          offset,
          limit,
        })
      }
      if (dupSetup) {
        return duplicates({
          id: datasetId,
          filters,
          keyColumns: dupSetup.columns,
          mode: dupSetup.mode,
          resolution: dupSetup.resolution,
          offset,
          limit,
        })
      }
      return query({ id: datasetId, filters, offset, limit })
    },
    [datasetId, filters, dupSetup, cmpSetup],
  )

  // New file, new filters, a new duplicate setup or a new comparison: everything
  // cached is stale.
  useEffect(() => {
    pages.current.clear()
    inflight.current.clear()
    order.current = []
    available.current = 0
    setStatus((s) => ({ ...s, matched: 0, available: 0, groups: 0, removed: 0, done: false, progress: 0, error: null }))
    bump((n) => n + 1)
  }, [datasetId, filters, dupSetup, cmpSetup])

  const applyStatus = useCallback((next) => {
    // More rows are available now, so pages that came back short may have grown.
    // Done outside the state updater: updaters must stay pure (StrictMode calls them twice).
    if (next.available > available.current) {
      for (const [key, page] of pages.current) {
        if (page.length < PAGE_SIZE) pages.current.delete(key)
      }
    }
    available.current = next.available
    setStatus(next)
  }, [])

  const fetchPage = useCallback(
    async (pageIndex) => {
      const key = pageIndex
      if (pages.current.has(key) || inflight.current.has(key)) return
      inflight.current.add(key)
      try {
        const { rows, ...rest } = await load(pageIndex * PAGE_SIZE, PAGE_SIZE)
        pages.current.set(key, rows)
        // Keep one entry per page, or LRU eviction could drop a page we just fetched.
        order.current = [...order.current.filter((k) => k !== key), key]
        while (order.current.length > MAX_CACHED_PAGES) {
          pages.current.delete(order.current.shift())
        }
        applyStatus(rest)
        bump((n) => n + 1)
      } catch (err) {
        setStatus((s) => ({ ...s, error: err.message }))
      } finally {
        inflight.current.delete(key)
      }
    },
    [load, applyStatus],
  )

  // Poll progress (limit 0 → status only, no row payload) until the scan finishes.
  useEffect(() => {
    if (!datasetId) return
    let cancelled = false
    let timer = null
    const tick = async () => {
      try {
        const { rows: _rows, ...rest } = await load(0, 0)
        if (cancelled) return
        applyStatus(rest)
        bump((n) => n + 1)
        if (!rest.done) timer = setTimeout(tick, POLL_MS)
      } catch (err) {
        if (!cancelled) setStatus((s) => ({ ...s, error: err.message }))
      }
    }
    tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [datasetId, load, applyStatus])

  const ensureRange = useCallback(
    (start, end) => {
      const first = Math.max(0, Math.floor(start / PAGE_SIZE))
      const last = Math.floor(Math.max(start, end - 1) / PAGE_SIZE)
      for (let p = first; p <= last; p++) fetchPage(p)
    },
    [fetchPage],
  )

  const getRow = useCallback((index) => {
    const page = pages.current.get(Math.floor(index / PAGE_SIZE))
    return page ? page[index % PAGE_SIZE] : undefined
  }, [])

  return { status, ensureRange, getRow, rowCount: status.available }
}
